import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Fab,
  CircularProgress
} from '@mui/material';
import {
  MonetizationOn,
  Calculate as CalculatorIcon,
  CreditCard,
  QrCode,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Schedule as ScheduleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  AccountBalance as BankIcon,
  PhoneAndroid as MobileIcon,
  Contactless as ContactlessIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Import setup dialogs
import CreditCardConfigDialog from '../dialogs/CreditCardConfigDialog.jsx';
import QRPaymentSetupDialog from '../dialogs/QRPaymentSetupDialog';
import DigitalWalletSetupDialog from '../dialogs/DigitalWalletSetupDialog';
import OnlineBankingSetupDialog from '../dialogs/OnlineBankingSetupDialog';

const MotionCard = motion(Card);
const MotionListItem = motion(ListItem);

// ✅ Asian Payment Method Icons
const AsianPaymentIcons = {
  WeChat: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8.5 5.5C4.4 5.5 1 8.4 1 12c0 2.1 1.2 4 3.2 5.3l-.6 2.9 3.1-1.5c.9.2 1.8.3 2.8.3 4.1 0 7.5-2.9 7.5-6.5S12.6 5.5 8.5 5.5z"/>
      <path d="M23 15.5c0-2.8-2.8-5-6.2-5-3.4 0-6.2 2.2-6.2 5s2.8 5 6.2 5c.8 0 1.6-.1 2.3-.3l2.5 1.2-.5-2.3c1.6-1 2.9-2.6 2.9-4.6z"/>
    </svg>
  ),
  Alipay: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
      <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0 8c-1.65 0-3-1.35-3-3s1.35-3 3-3 3 1.35 3 3-1.35 3-3 3z"/>
    </svg>
  ),
  TouchNGo: () => <ContactlessIcon />,
  GrabPay: () => <MobileIcon />,
  ShopeePay: () => <PaymentIcon />,
  FPX: () => <BankIcon />,
  PayLah: () => <MobileIcon />,
  BigPay: () => <CreditCard />,
  ZaloPay: () => <MobileIcon />
};

// ✅ Get icon component for payment method
const getPaymentIcon = (iconName) => {
  const iconMap = {
    MonetizationOn: MonetizationOn,
    Calculate: CalculatorIcon,
    CreditCard: CreditCard,
    QrCode: QrCode,
    Payment: PaymentIcon,
    ...AsianPaymentIcons
  };
  
  const IconComponent = iconMap[iconName] || PaymentIcon;
  return <IconComponent />;
};

// ✅ ALL AVAILABLE PAYMENT METHODS (for add dialog)
const AVAILABLE_PAYMENT_METHODS = {
  // Default QR Payment
  qrCode: {
    name: 'QR Payment',
    region: 'Global',
    icon: 'QrCode',
    color: '#f59e0b',
    category: 'qr_payment',
    description: 'PayNow, QR payments with manual confirmation',
    setupRequired: false
  },
  
  // Credit Card
  creditCard: {
    name: 'Credit Card',
    region: 'Global',
    icon: 'CreditCard',
    color: '#8b5cf6',
    category: 'credit_card',
    description: 'Integrated credit card processing',
    setupRequired: true
  },
  
  // Asian Digital Wallets
  wechat: {
    name: 'WeChat Pay',
    region: 'China/Malaysia',
    icon: 'WeChat',
    color: '#00d924',
    category: 'digital_wallet',
    description: 'Popular in China and Malaysia',
    setupRequired: true
  },
  alipay: {
    name: 'Alipay',
    region: 'China/Malaysia', 
    icon: 'Alipay',
    color: '#1677ff',
    category: 'digital_wallet',
    description: 'Popular in China and Malaysia',
    setupRequired: true
  },
  touchngo: {
    name: 'Touch \'n Go eWallet',
    region: 'Malaysia',
    icon: 'TouchNGo',
    color: '#1e3a8a',
    category: 'digital_wallet',
    description: 'Malaysia\'s leading eWallet',
    setupRequired: true
  },
  grabpay: {
    name: 'GrabPay',
    region: 'Southeast Asia',
    icon: 'GrabPay',
    color: '#00b14f',
    category: 'digital_wallet',
    description: 'Popular across Southeast Asia',
    setupRequired: true
  },
  shopeepay: {
    name: 'ShopeePay',
    region: 'Southeast Asia',
    icon: 'ShopeePay',
    color: '#ee4d2d',
    category: 'digital_wallet',
    description: 'Growing across Southeast Asia',
    setupRequired: true
  },
  zalopay: {
    name: 'ZaloPay',
    region: 'Vietnam',
    icon: 'ZaloPay',
    color: '#0068ff',
    category: 'digital_wallet',
    description: 'Leading payment app in Vietnam',
    setupRequired: true
  },
  paylah: {
    name: 'PayLah!',
    region: 'Singapore',
    icon: 'PayLah',
    color: '#dc2626',
    category: 'digital_wallet',
    description: 'DBS Singapore digital wallet',
    setupRequired: true
  },
  bigpay: {
    name: 'BigPay',
    region: 'Malaysia',
    icon: 'BigPay',
    color: '#7c3aed',
    category: 'digital_wallet',
    description: 'AirAsia\'s financial service',
    setupRequired: true
  },
  
  // Online Banking
  fpx: {
    name: 'FPX Online Banking',
    region: 'Malaysia',
    icon: 'FPX',
    color: '#2563eb',
    category: 'online_banking',
    description: 'Malaysia online banking network',
    setupRequired: true
  },
  paynowBanking: {
    name: 'PayNow Banking',
    region: 'Singapore',
    icon: 'FPX',
    color: '#2563eb',
    category: 'online_banking',
    description: 'Singapore instant payment with banking',
    setupRequired: true
  },
  duitnow: {
    name: 'DuitNow Banking',
    region: 'Malaysia',
    icon: 'FPX',
    color: '#2563eb',
    category: 'online_banking',
    description: 'Malaysia real-time payment service',
    setupRequired: true
  },
  promptpayBanking: {
    name: 'PromptPay Banking',
    region: 'Thailand',
    icon: 'FPX',
    color: '#2563eb',
    category: 'online_banking',
    description: 'Thailand national e-payment with banking',
    setupRequired: true
  }
};

const PaymentMethodsManager = ({ settings, updatePaymentMethod }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    // ✅ Add local state to force re-renders
  const [refreshKey, setRefreshKey] = useState(0);
  const [deletingMethods, setDeletingMethods] = useState(new Set());


  // ✅ State for different setup dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCreditCardConfig, setShowCreditCardConfig] = useState(false);
  const [showQRSetupDialog, setShowQRSetupDialog] = useState(false);
  const [showDigitalWalletSetupDialog, setShowDigitalWalletSetupDialog] = useState(false);
  const [showOnlineBankingSetupDialog, setShowOnlineBankingSetupDialog] = useState(false);
  const [selectedAsianMethod, setSelectedAsianMethod] = useState('');
  const [selectedSetupMethod, setSelectedSetupMethod] = useState(null);

  // ✅ Define default/protected payment methods that cannot be deleted
  const PROTECTED_METHODS = ['exactCash', 'calculateChange', 'calculate_change'];

  // ✅ Get appropriate setup dialog for payment method
  const getSetupDialogType = (methodId) => {
    // QR-based payments
    if (['qrCode', 'paynow', 'qrPayment'].includes(methodId)) {
      return 'qr';
    }
    
    // Credit card
    if (['creditCard', 'credit_card'].includes(methodId)) {
      return 'credit_card';
    }
    
    // Digital wallets - PayLah gets its own setup
    if (methodId === 'paylah') {
      return 'paylah_wallet'; // Special setup for PayLah
    }
    
    // Other digital wallets
    if (['wechat', 'alipay', 'touchngo', 'grabpay', 'shopeepay', 'zalopay', 'bigpay'].includes(methodId)) {
      return 'digital_wallet';
    }
    
    // Online banking
    if (['fpx', 'paynowBanking', 'duitnow', 'promptpayBanking'].includes(methodId)) {
      return 'online_banking';
    }
    
    return null;
  };

  // ✅ FIXED: Consolidate duplicate payment methods and remove duplicates
 const consolidatePaymentMethods = useCallback((paymentMethods) => {
  if (!paymentMethods || typeof paymentMethods !== 'object') return {};
  
  const consolidated = { ...paymentMethods };
  
  // ✅ Remove duplicate calculate change entries
  if (consolidated.calculateChange && consolidated.calculate_change) {
    const primary = consolidated.calculateChange.name ? 'calculateChange' : 'calculate_change';
    const secondary = primary === 'calculateChange' ? 'calculate_change' : 'calculateChange';
    
    consolidated[primary] = {
      ...consolidated[secondary],
      ...consolidated[primary],
      id: 'calculate_change',
      name: 'Calculate Change',
      icon: 'Calculate'
    };
    
    delete consolidated[secondary];
  }
  
  // ✅ FIXED: Sort payment methods with proper order
  const sortedEntries = Object.entries(consolidated).sort((a, b) => {
    const [methodIdA, methodA] = a;
    const [methodIdB, methodB] = b;
    
    // Define priority order
    const getPriority = (methodId, method) => {
      // Default methods always come first
      if (methodId === 'exactCash') return 1;
      if (methodId === 'calculate_change' || methodId === 'calculateChange') return 2;
      
      // Then enabled methods
      if (method.enabled) return method.order || 50;
      
      // Then disabled methods
      return method.order || 100;
    };
    
    return getPriority(methodIdA, methodA) - getPriority(methodIdB, methodB);
  });
  
  // Convert back to object
  return Object.fromEntries(sortedEntries);
}, []); // ✅ Remove forceUpdate dependency // ✅ Add forceUpdate as dependency

  // ✅ Handle setup button click
  const handleSetupClick = (methodId, method) => {
    const setupType = getSetupDialogType(methodId);
    console.log('🔍 PaymentMethodsManager: Setup click:', {
      methodId,
      methodName: method.name,
      setupType
    });
    
    setSelectedSetupMethod({ 
      id: methodId, 
      name: method.name,
      ...method 
    });
    
    switch (setupType) {
      case 'qr':
        setShowQRSetupDialog(true);
        break;
      case 'paylah_wallet':
        // PayLah uses digital wallet setup but with PayLah-specific config
        setShowDigitalWalletSetupDialog(true);
        break;
      case 'digital_wallet':
        setShowDigitalWalletSetupDialog(true);
        break;
      case 'online_banking':
        setShowOnlineBankingSetupDialog(true);
        break;
      case 'credit_card':
        setShowCreditCardConfig(true);
        break;
      default:
        console.warn('No setup dialog available for:', methodId);
    }
  };

  // ✅ FIXED: Handle delete payment method - only custom methods
  const handleDeleteMethod = async (methodId) => {
    try {
      console.log('🗑️ Deleting payment method:', methodId);
      
      // ✅ Check if method can be deleted
      if (PROTECTED_METHODS.includes(methodId)) {
        alert(t('posConfig.paymentMethods.cannotDelete') || 'This payment method cannot be deleted as it is a system default.');
        return;
      }
      
      // ✅ Check if already deleting
      if (deletingMethods.has(methodId)) {
        console.log('⚠️ Already deleting this method');
        return;
      }
      
      // ✅ Confirm deletion
      const confirmed = window.confirm(
        (t('posConfig.paymentMethods.confirmDelete') || 'Are you sure you want to delete "{name}"?')
        .replace('{name}', settings?.paymentMethods?.[methodId]?.name || methodId)
      );
      if (!confirmed) return;
      
      // ✅ Mark as deleting
      setDeletingMethods(prev => new Set([...prev, methodId]));
      
      // ✅ Create new object without the deleted method
      const currentMethods = { ...settings?.paymentMethods };
      delete currentMethods[methodId];
      
      console.log('🔄 Updated methods after deletion:', Object.keys(currentMethods));
      console.log('🔄 Deleted method:', methodId);
      console.log('🔄 Remaining methods:', Object.keys(currentMethods));
      
      // ✅ CRITICAL: Use __ALL__ for bulk update to force re-render
      await updatePaymentMethod('__ALL__', currentMethods, true);
      
      // ✅ Trigger refresh
      setRefreshKey(prev => prev + 1);
      
      console.log('✅ Payment method deleted successfully');
      
    } catch (error) {
      console.error('❌ Error deleting payment method:', error);
      alert(t('posConfig.paymentMethods.deleteFailed') || 'Failed to delete payment method. Please try again.');
    } finally {
      // ✅ Remove from deleting set
      setDeletingMethods(prev => {
        const newSet = new Set(prev);
        newSet.delete(methodId);
        return newSet;
      });
    }
  };

  // ✅ FIXED: Check if payment method can be deleted
  const canDeleteMethod = (methodId) => {
    const method = settings?.paymentMethods?.[methodId];
    const isProtected = PROTECTED_METHODS.includes(methodId);
    const isCustom = method?.isCustom || method?.order >= 100;
    
    console.log('🔍 Can delete check:', {
      methodId,
      isProtected,
      isCustom,
      canDelete: !isProtected && isCustom
    });
    
    return !isProtected && isCustom;
  };

  // ✅ Enhanced method update to handle deletion
  const handleMethodUpdate = useCallback((methodId, updates, isDelete = false) => {
  console.log('🔄 Updating payment method:', methodId, updates, isDelete);
  
  if (isDelete) {
    // Handle deletion - pass the entire updated methods object
    const currentMethods = { ...settings?.paymentMethods };
    delete currentMethods[methodId];
    
    console.log('🗑️ Deleting method:', methodId);
    console.log('📝 Updated methods after deletion:', Object.keys(currentMethods));
    
    // Update with the remaining methods using the bulk update approach
    updatePaymentMethod('__ALL__', currentMethods, true);
  } else {
    // Normal update
    if (methodId === 'calculate_change' || methodId === 'calculateChange') {
      updatePaymentMethod('calculate_change', {
        ...updates,
        id: 'calculate_change',
        name: updates.name || 'Calculate Change',
        icon: 'Calculate'
      });
    } else {
      updatePaymentMethod(methodId, updates);
    }
  }
}, [updatePaymentMethod, settings]); 

  // ✅ NEW: Add Asian Payment Method
  const handleAddPaymentMethod = async () => {
    if (!selectedAsianMethod) return;
    
    try {
      const methodConfig = AVAILABLE_PAYMENT_METHODS[selectedAsianMethod];
      
      // ✅ Get next order number for custom methods
      const existingMethods = Object.values(settings?.paymentMethods || {});
      const customMethods = existingMethods.filter(method => method.isCustom || method.order >= 100);
      const nextOrder = customMethods.length > 0 
        ? Math.max(...customMethods.map(m => m.order || 100)) + 1 
        : 101;

      const newMethod = {
        id: selectedAsianMethod,
        name: methodConfig.name,
        icon: methodConfig.icon,
        enabled: false, // ✅ Disabled by default
        visible: true,
        comingSoon: false,
        category: methodConfig.category,
        region: methodConfig.region,
        description: methodConfig.description,
        setupRequired: methodConfig.setupRequired,
        configured: !methodConfig.setupRequired,
        isCustom: true, // ✅ Mark as custom (can be deleted)
        isDefault: false,
        order: nextOrder,
        manualConfirm: ['digital_wallet', 'qr_payment'].includes(methodConfig.category)
      };

      // ✅ Add to current methods
      const updatedMethods = {
        ...settings?.paymentMethods,
        [selectedAsianMethod]: newMethod
      };

      console.log('🆕 Adding new payment method:', selectedAsianMethod, newMethod);
      
      // ✅ Update via bulk update
      await updatePaymentMethod('__ALL__', updatedMethods, true);
      
      setSelectedAsianMethod('');
      setShowAddDialog(false);
      
      console.log('✅ Payment method added successfully');
      
    } catch (error) {
      console.error('❌ Error adding payment method:', error);
      alert('Failed to add payment method. Please try again.');
    }
  };

  // ✅ Get available methods to show in add dialog (exclude already added ones)
  const getAvailableMethodsForAdd = () => {
    const currentMethodIds = Object.keys(settings?.paymentMethods || {});
    
    return Object.entries(AVAILABLE_PAYMENT_METHODS).filter(([methodId]) => {
      // Don't show if already added
      return !currentMethodIds.includes(methodId);
    });
  };

  // ✅ Get payment method color
  const getPaymentColor = (methodId) => {
    switch (methodId) {
      case 'exactCash': return '#10b981';
      case 'calculate_change':
      case 'calculateChange': return '#3b82f6';
      case 'qrCode': return '#f59e0b';
      case 'creditCard': return '#8b5cf6';
      case 'wechat': return '#00d924';
      case 'alipay': return '#1677ff';
      case 'touchngo': return '#1e3a8a';
      case 'grabpay': return '#00b14f';
      case 'shopeepay': return '#ee4d2d';
      case 'paylah': return '#dc2626';
      default: return '#6b7280';
    }
  };

  // ✅ Check if method is QR-based
  const isQRMethod = (methodId) => {
    return ['qrCode', 'paynow', 'wechat', 'alipay', 'touchngo', 'grabpay', 'shopeepay', 'zalopay', 'paylah'].includes(methodId);
  };

  // ✅ Memoized payment methods to prevent unnecessary re-renders
   const paymentMethods = useMemo(() => {
    console.log('🔄 Recomputing payment methods, refreshKey:', refreshKey);
    const methods = consolidatePaymentMethods(settings?.paymentMethods || {});
    console.log('📋 Available payment methods:', Object.keys(methods));
    return methods;
  }, [settings?.paymentMethods, refreshKey]); 

  // ✅ Add effect to log when paymentMethods changes
  useEffect(() => {
    console.log('🔄 Settings changed, triggering refresh');
    setRefreshKey(prev => prev + 1);
  }, [settings?.paymentMethods]);

  if (Object.keys(paymentMethods).length === 0) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t('posConfig.paymentMethods.title') || 'Payment Methods'}
        </Typography>
        <Alert severity="info">
          {t('posConfig.paymentMethods.noPaymentMethods') || 'No payment methods configured. Contact your system administrator to set up payment methods.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* ✅ Enhanced Header with Add Button */}
      <Box sx={{ mb: isMobile ? 2 : 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
            {t('posConfig.paymentMethods.title') || 'Payment Methods'}
          </Typography>
          <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
            {t('posConfig.paymentMethods.description') || 'Configure payment methods and integrations for your POS system'}
          </Typography>
        </Box>
        
        {/* Add Payment Method Button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            // Check if there are methods available to add
            if (getAvailableMethodsForAdd().length === 0) {
              alert('All available payment methods have been added to your system.');
              return;
            }
            setShowAddDialog(true);
          }}
          size={isMobile ? "small" : "medium"}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)',
            }
          }}
        >
          Add Method ({getAvailableMethodsForAdd().length} available)
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: isMobile ? 2 : 3,
          borderRadius: 2,
          fontSize: isMobile ? '0.8rem' : '0.875rem'
        }}
      >
        {t('posConfig.paymentMethods.infoAlert') || 'QR payments work like exact cash - complete payment externally, then click the payment method to confirm. Credit card and digital wallets require setup.'}
      </Alert>

      {/* Payment Methods Layout */}
      {isMobile ? (
        // Mobile List Layout
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <List sx={{ p: 0 }}>
            {Object.entries(paymentMethods).map(([methodId, method], index) => {
              const methodColor = getPaymentColor(methodId);
              const isLast = index === Object.entries(paymentMethods).length - 1;
              const needsSetup = method.setupRequired && !method.configured;
              const isQR = isQRMethod(methodId);
              const canSetup = method.setupRequired || method.isCustom;
              
              return (
                <React.Fragment key={methodId}>
                  <MotionListItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    sx={{
                      py: 2,
                      px: 2,
                      borderLeft: `4px solid ${methodColor}`,
                      bgcolor: method.enabled && !needsSetup ? `${methodColor}05` : 'grey.50',
                      opacity: needsSetup ? 0.6 : 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Avatar sx={{ 
                        width: 36, 
                        height: 36, 
                        bgcolor: method.enabled && !needsSetup ? `${methodColor}15` : 'grey.200',
                        color: method.enabled && !needsSetup ? methodColor : 'grey.500'
                      }}>
                        {React.cloneElement(getPaymentIcon(method.icon), { sx: { fontSize: 18 } })}
                      </Avatar>
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                              {method.name}
                            </Typography>
                            {needsSetup && (
                              <Chip
                                label="Setup Required"
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                            {isQR && (
                              <Chip
                                label="Manual Confirm"
                                size="small"
                                color="info"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                            {method.isCustom && (
                              <Chip
                                label="Custom"
                                size="small"
                                color="secondary"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {method.description || method.region || 'Standard payment method'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                            <Chip
                              icon={method.enabled ? <ActiveIcon /> : <InactiveIcon />}
                              label={method.enabled ? 'Active' : 'Inactive'}
                              size="small"
                              color={method.enabled ? 'success' : 'default'}
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                            />
                          </Box>
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        {/* Toggle Switch */}
                        <Switch
                          checked={method.enabled || false}
                          onChange={(e) => handleMethodUpdate(methodId, { enabled: e.target.checked })}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: methodColor,
                              '& + .MuiSwitch-track': { backgroundColor: methodColor }
                            }
                          }}
                        />
                        
                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {/* Setup Button */}
                          {canSetup && (
                            <Tooltip title={t('posConfig.paymentMethods.setup') || 'Setup Payment Method'}>
                              <span>
                                <IconButton
                                  onClick={() => handleSetupClick(methodId, method)}
                                  size="small"
                                  color="primary"
                                  disabled={!method.enabled}
                                  sx={{ 
                                    bgcolor: method.enabled ? `${methodColor}10` : 'grey.100',
                                    opacity: method.enabled ? 1 : 0.5
                                  }}
                                >
                                  <SettingsIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          
                          {/* ✅ FIXED: Delete Button - properly check and call delete */}
                          {canDeleteMethod(methodId) && (
                          <Tooltip title={t('posConfig.paymentMethods.delete') || 'Delete Payment Method'}>
                            <IconButton
                              onClick={() => handleDeleteMethod(methodId)}
                              size="small"
                              color="error"
                              disabled={deletingMethods.has(methodId)}
                              sx={{ 
                                opacity: deletingMethods.has(methodId) ? 0.5 : 0.7,
                                '&:hover': { 
                                  opacity: 1, 
                                  bgcolor: 'error.lighter',
                                  transform: deletingMethods.has(methodId) ? 'none' : 'scale(1.1)'
                                }
                              }}
                            >
                              {deletingMethods.has(methodId) ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <DeleteIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        </Box>
                      </Box>
                    </ListItemSecondaryAction>
                  </MotionListItem>
                  {!isLast && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      ) : (
        // Desktop/Tablet Grid Layout
        <Grid container spacing={isTablet ? 2 : 3}>
          {Object.entries(paymentMethods).map(([methodId, method], index) => {
            const methodColor = getPaymentColor(methodId);
            const needsSetup = method.setupRequired && !method.configured;
            const isQR = isQRMethod(methodId);
            const canSetup = method.setupRequired || method.isCustom;
            
            return (
              <Grid item xs={12} md={6} lg={4} key={methodId}>
                <MotionCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  sx={{
                    height: '100%',
                    border: `1px solid ${methodColor}20`,
                    borderLeft: `4px solid ${methodColor}`,
                    borderRadius: 3,
                    opacity: needsSetup ? 0.8 : 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 8px 24px ${methodColor}25`,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header with Delete Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{
                          width: 48,
                          height: 48,
                          bgcolor: method.enabled && !needsSetup ? `${methodColor}15` : 'grey.200',
                          color: method.enabled && !needsSetup ? methodColor : 'grey.500'
                        }}>
                          {getPaymentIcon(method.icon)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
                            {method.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {needsSetup && (
                              <Chip label="Setup Required" color="warning" size="small" />
                            )}
                            {isQR && (
                              <Chip label="Manual Confirm" color="info" size="small" />
                            )}
                            {method.isCustom && (
                              <Chip label="Custom" color="secondary" size="small" />
                            )}
                            {method.region && (
                              <Chip label={method.region} color="default" size="small" />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Setup Button */}
                        {canSetup && (
                          <Tooltip title="Setup Payment Method">
                            <span>
                              <IconButton
                                onClick={() => handleSetupClick(methodId, method)}
                                size="small"
                                color="primary"
                                disabled={!method.enabled}
                                sx={{ 
                                  bgcolor: method.enabled ? `${methodColor}10` : 'grey.100',
                                  opacity: method.enabled ? 1 : 0.5
                                }}
                              >
                                <SettingsIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        
                        {/* ✅ FIXED: Delete Button - properly check and call delete */}
                        {canDeleteMethod(methodId) && (
                          <Tooltip title="Delete Payment Method">
                            <IconButton
                              onClick={() => handleDeleteMethod(methodId)}
                              size="small"
                              color="error"
                              sx={{ 
                                opacity: 0.7,
                                '&:hover': { 
                                  opacity: 1, 
                                  bgcolor: 'error.lighter',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {/* Controls */}
                    <Stack spacing={2}>
                      {/* ✅ Always show toggle - can be enabled/disabled */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={method.enabled || false}
                            onChange={(e) => handleMethodUpdate(methodId, { enabled: e.target.checked })}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: methodColor,
                                '& + .MuiSwitch-track': { backgroundColor: methodColor }
                              }
                            }}
                          />
                        }
                        label="Enabled"
                      />

                      {/* ✅ Setup fields - greyed out when disabled */}
                      <TextField
                        fullWidth
                        label="Display Name"
                        value={method.name || ''}
                        onChange={(e) => handleMethodUpdate(methodId, { name: e.target.value })}
                        disabled={!method.enabled} // ✅ Greyed out when disabled
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            opacity: method.enabled ? 1 : 0.5
                          }
                        }}
                      />

                      {method.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {method.description}
                        </Typography>
                      )}
                    </Stack>

                    {/* Status Messages */}
                    {needsSetup && method.enabled && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.lighter', borderRadius: 2 }}>
                        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                          ⚙️ Setup required before this payment method can be used in POS.
                        </Typography>
                      </Box>
                    )}

                    {isQR && method.enabled && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 2 }}>
                        <Typography variant="body2" color="info.main" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                          📱 Works like exact cash - complete payment externally, then click to confirm.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </MotionCard>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ✅ NEW: Add Payment Method Dialog */}
      <Dialog 
        open={showAddDialog} 
        onClose={() => setShowAddDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Payment Method</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose from available payment methods to add to your POS system
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Payment Method</InputLabel>
            <Select
              value={selectedAsianMethod}
              onChange={(e) => setSelectedAsianMethod(e.target.value)}
              label="Select Payment Method"
            >
              {getAvailableMethodsForAdd().map(([key, method]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: `${method.color}15`, color: method.color }}>
                      {getPaymentIcon(method.icon)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">{method.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {method.region} • {method.description}
                      </Typography>
                    </Box>
                    {method.setupRequired && (
                      <Chip
                        label="Setup Required"
                        size="small"
                        color="warning"
                        sx={{ fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ✅ Show if no methods available to add */}
          {getAvailableMethodsForAdd().length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>All payment methods have been added!</strong>
                <br />You can enable/disable or delete existing methods to manage your payment options.
              </Typography>
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> 
              <br />• <strong>QR Payments</strong>: Work with manual confirmation - complete payment externally, then click to confirm
              <br />• <strong>Digital Wallets & Credit Cards</strong>: Require setup before they can be used
              <br />• <strong>Default methods</strong> (Exact Cash, Calculate Change) cannot be removed
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddPaymentMethod}
            variant="contained"
            disabled={!selectedAsianMethod}
          >
            Add Payment Method
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Credit Card Configuration Dialog */}
      <CreditCardConfigDialog
        open={showCreditCardConfig}
        onClose={() => setShowCreditCardConfig(false)}
        onSave={(config) => {
          handleMethodUpdate('creditCard', { 
            ...config, 
            configured: true,
            integrationConfigured: true 
          });
          setShowCreditCardConfig(false);
        }}
        currentConfig={paymentMethods.creditCard || {}}
      />

      {/* ✅ NEW: QR Payment Setup Dialog */}
      <QRPaymentSetupDialog
        open={showQRSetupDialog}
        onClose={() => {
          setShowQRSetupDialog(false);
          setSelectedSetupMethod(null);
        }}
        onSave={(config) => {
          handleMethodUpdate(selectedSetupMethod.id, { 
            ...config, 
            configured: true,
            setupCompleted: true 
          });
          setShowQRSetupDialog(false);
          setSelectedSetupMethod(null);
        }}
        currentConfig={selectedSetupMethod}
        paymentMethod={selectedSetupMethod}
      />

      {/* ✅ NEW: Digital Wallet Setup Dialog */}
      <DigitalWalletSetupDialog
        open={showDigitalWalletSetupDialog}
        onClose={() => {
          setShowDigitalWalletSetupDialog(false);
          setSelectedSetupMethod(null);
        }}
        onSave={(config) => {
          handleMethodUpdate(selectedSetupMethod.id, { 
            ...config, 
            configured: true,
            setupCompleted: true 
          });
          setShowDigitalWalletSetupDialog(false);
          setSelectedSetupMethod(null);
        }}
        currentConfig={selectedSetupMethod}
        paymentMethod={selectedSetupMethod}
      />

      {/* ✅ NEW: Online Banking Setup Dialog */}
      <OnlineBankingSetupDialog
        open={showOnlineBankingSetupDialog}
        onClose={() => {
          setShowOnlineBankingSetupDialog(false);
          setSelectedSetupMethod(null);
        }}
        onSave={(config) => {
          handleMethodUpdate(selectedSetupMethod.id, { 
            ...config, 
            configured: true,
            setupCompleted: true 
          });
          setShowOnlineBankingSetupDialog(false);
          setSelectedSetupMethod(null);
        }}
        currentConfig={selectedSetupMethod}
        paymentMethod={selectedSetupMethod}
      />

      {/* Usage Examples */}
      <Paper sx={{ 
        mt: isMobile ? 3 : 4, 
        p: isMobile ? 2 : 3,
        bgcolor: 'primary.lighter',
        borderRadius: 3,
        border: '1px solid rgba(102, 126, 234, 0.1)'
      }}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
          Payment Method Types
        </Typography>
        <Grid container spacing={isMobile ? 1 : 2}>
          {[
            {
              title: 'Exact Cash',
              desc: 'Process exact cash payments without calculating change'
            },
            {
              title: 'Calculate Change',
              desc: 'Cash payments with automatic change calculation'
            },
            {
              title: 'QR Payments',
              desc: 'Manual confirmation after external QR payment (PayNow, WeChat, etc.)'
            },
            {
              title: 'Credit Card',
              desc: 'Integrated credit card processing (requires setup)'
            }
          ].map((example, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box sx={{ mb: isMobile ? 1 : 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                  {example.title}
                </Typography>
                <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {example.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};

export default PaymentMethodsManager;